import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import Icon from './AppIcon';
import { Checkbox } from './ui/Checkbox';
import { getCookieConsent } from '../utils/cookies';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookieConsent();
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  // Listen for custom event to open preferences (for footer link)
  useEffect(() => {
    const handleOpenPreferences = () => {
      setShowPreferences(true);
      setShowBanner(true); // Show banner if hidden
    };

    window.addEventListener('openCookiePreferences', handleOpenPreferences);
    return () => {
      window.removeEventListener('openCookiePreferences', handleOpenPreferences);
    };
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    // Trigger event for analytics/marketing initialization
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consent }));
  };

  const handleRejectAll = () => {
    const consent = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    // Trigger event for analytics/marketing cleanup
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consent }));
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = (preferences) => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    setShowPreferences(false);
    // Trigger event for analytics/marketing initialization/cleanup
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consent }));
  };

  const handleLinkClick = (href) => {
    if (href?.startsWith('/')) {
      const pageKey = href.substring(1);
      const legalContent = t('footer.legalContent', { returnObjects: true });
      if (legalContent[pageKey]) {
        // Open cookie policy modal via footer pattern
        window.dispatchEvent(new CustomEvent('openLegalModal', { 
          detail: { pageKey, content: legalContent[pageKey] } 
        }));
      }
    }
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[160] p-4 bg-background border-t border-border shadow-2xl">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm text-text-secondary">
              {t('cookieConsent.message')}{' '}
              <button
                onClick={() => handleLinkClick('/cookies')}
                className="underline ml-1 hover:text-primary transition-colors"
              >
                {t('cookieConsent.learnMore')}
              </button>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCustomize}
            >
              {t('cookieConsent.customize')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRejectAll}
            >
              {t('cookieConsent.reject')}
            </Button>
            <Button
              size="sm"
              onClick={handleAcceptAll}
            >
              {t('cookieConsent.accept')}
            </Button>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <CookiePreferencesModal
          onSave={handleSavePreferences}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </>
  );
};

// Cookie Preferences Modal Component
const CookiePreferencesModal = ({ onSave, onClose }) => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(() => {
    const consent = getCookieConsent();
    return {
      essential: true, // Always true, can't be disabled
      analytics: consent?.analytics ?? false,
      marketing: consent?.marketing ?? false
    };
  });

  const handleToggle = (key) => {
    if (key === 'essential') return; // Can't disable essential
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="fixed inset-0 z-[170] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-2xl max-w-2xl w-full shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
          <h2 className="text-lg sm:text-2xl font-semibold text-text-primary">
            {t('cookieConsent.preferences.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors min-h-[44px] min-w-[44px]"
            aria-label={t('common.close')}
          >
            <Icon name="X" size={24} className="text-text-secondary" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
          <p className="text-text-secondary mb-6 text-sm sm:text-base">
            {t('cookieConsent.preferences.description')}
          </p>

          <div className="space-y-4">
            {/* Essential Cookies */}
            <div className="flex items-start justify-between p-4 border border-border rounded-lg gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                  {t('cookieConsent.preferences.essential.title')}
                </h3>
                <p className="text-sm text-text-secondary">
                  {t('cookieConsent.preferences.essential.description')}
                </p>
              </div>
              <div className="shrink-0">
                <Checkbox
                  checked={preferences.essential}
                  disabled
                  size="default"
                />
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between p-4 border border-border rounded-lg gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                  {t('cookieConsent.preferences.analytics.title')}
                </h3>
                <p className="text-sm text-text-secondary">
                  {t('cookieConsent.preferences.analytics.description')}
                </p>
              </div>
              <div className="shrink-0">
                <Checkbox
                  checked={preferences.analytics}
                  onChange={() => handleToggle('analytics')}
                  size="default"
                />
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between p-4 border border-border rounded-lg gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-text-primary mb-1 text-sm sm:text-base">
                  {t('cookieConsent.preferences.marketing.title')}
                </h3>
                <p className="text-sm text-text-secondary">
                  {t('cookieConsent.preferences.marketing.description')}
                </p>
              </div>
              <div className="shrink-0">
                <Checkbox
                  checked={preferences.marketing}
                  onChange={() => handleToggle('marketing')}
                  size="default"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSave(preferences)}>
            {t('cookieConsent.preferences.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;

